import React from "react";
import {
  DeleteIcon,
  PlusIcon,
  FormsIcon,
  HistoryIcon,
  PrintIcon,
  CalendarIcon,
  AlertCircleIcon,
} from "../../../../Icons";
import { displayDateUS } from "../../../../utils/dateUtils";
import { clinicalFormScheduleDate } from "./sortClinicalForms";
import { clinicalFormPrintUrl } from "./clinicalFormPrint";
import ClinicalFormStatusBadge from "./ClinicalFormStatusBadge";
import ClinicalFormMarksRow from "./ClinicalFormMarksRow";
import ClinicalFormAttachmentPin from "./ClinicalFormAttachmentPin";
import { PATIENT_THEME } from "../../patientTheme";
import {
  isVisitUnauthorized,
  visitAuthorizationTitle,
  UNAUTHORIZED_TEXT,
  UNAUTHORIZED_TEXT_MUTED,
} from "../../visitAuthorization";

/** One shared box for every row action, so the icons line up exactly. */
const actionBtn =
  "inline-flex items-center justify-center h-6 w-6 shrink-0 rounded text-slate-600 hover:text-indigo-700 hover:bg-indigo-50 transition-colors";

/** Form list with multi-select, marks, attachment pin, and row actions. */
const ClinicalFormList = ({
  forms,
  loading,
  canEdit,
  /** Deleting removes the form and its scheduled visit — Admin/OA only. */
  canDelete = false,
  canManageFlags = false,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  onOpen,
  onDelete,
  onViewAudit,
  onRemoveMark,
  onAddClick,
  addLabel,
  patientId,
  onAttachmentsChanged,
  showSelection = true,
  showMarks = true,
  showAudit = true,
  showAttachments = true,
  onPrint,
}) => {
  const [removingKey, setRemovingKey] = React.useState(null);
  if (loading) {
    return (
      <p className="px-3 py-6 text-center text-sm text-slate-400 animate-pulse">
        Loading forms...
      </p>
    );
  }

  if (!forms.length) {
    return (
      <div className="px-3 py-8 text-center">
        <FormsIcon size={26} className="mx-auto text-slate-300 mb-2" />
        <p className="text-sm font-medium text-slate-500">No forms yet</p>
        {canEdit && (
          <button
            type="button"
            onClick={onAddClick}
            className={`mt-2.5 inline-flex items-center gap-1 h-7 px-2.5 text-xs font-semibold border bg-white rounded-md transition-colors ${PATIENT_THEME.section.addTone}`}
          >
            <PlusIcon size={12} />
            {addLabel}
          </button>
        )}
      </div>
    );
  }

  const allSelected =
    forms.length > 0 && forms.every((f) => selectedIds?.has(f.id));
  const someSelected = forms.some((f) => selectedIds?.has(f.id));

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm min-w-[720px]">
        <thead className={`sticky top-0 z-10 ${PATIENT_THEME.tableHead.row}`}>
          <tr
            className={`border-b text-left text-[11px] uppercase tracking-wide ${PATIENT_THEME.tableHead.className}`}
          >
            {showSelection && (
              <th className="px-2 py-1.5 font-semibold w-9">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = !allSelected && someSelected;
                  }}
                  onChange={() => onToggleSelectAll?.(!allSelected)}
                  className="h-3.5 w-3.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-200"
                  aria-label="Select all forms"
                />
              </th>
            )}
            <th className="px-3 py-1.5 font-semibold">Form Name</th>
            <th className="px-3 py-1.5 font-semibold hidden sm:table-cell">
              Filled By
            </th>
            <th className="px-3 py-1.5 font-semibold hidden md:table-cell">
              Role
            </th>
            <th className="px-3 py-1.5 font-semibold">Status</th>
            <th className="px-3 py-1.5 font-semibold">
              Schedule Date
            </th>
            {showMarks && (
              <th className="px-3 py-1.5 font-semibold hidden lg:table-cell">
                Flags
              </th>
            )}
            <th className="px-3 py-1.5 font-semibold text-right w-36">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {forms.map((form) => {
            const checked = selectedIds?.has(form.id);
            const marks = form.marks || [];
            const visit = form.scheduledVisit || null;
            const unauthorized = isVisitUnauthorized(visit?.authorization);
            // Placeholders stay subordinate to real values in either palette.
            const emDash = unauthorized
              ? UNAUTHORIZED_TEXT_MUTED
              : "text-slate-300";
            return (
              <tr
                key={form.id}
                className={`border-b border-slate-100 last:border-0 hover:bg-slate-50/70 ${
                  showSelection && checked ? "bg-indigo-50/40" : ""
                }`}
              >
                {showSelection && (
                  <td className="px-2 py-1.5">
                    <input
                      type="checkbox"
                      checked={Boolean(checked)}
                      onChange={() => onToggleSelect?.(form.id)}
                      className="h-3.5 w-3.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-200"
                      aria-label={`Select ${form.formName}`}
                    />
                  </td>
                )}
                <td className="px-3 py-1.5">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <button
                      type="button"
                      onClick={() => onOpen(form)}
                      className={`font-medium hover:underline text-left truncate ${
                        unauthorized ? UNAUTHORIZED_TEXT : "text-indigo-700"
                      }`}
                    >
                      {form.formName}
                    </button>
                    {showAttachments ? (
                      <ClinicalFormAttachmentPin
                        patientId={patientId}
                        form={form}
                        canEdit={canEdit}
                        onChanged={onAttachmentsChanged}
                      />
                    ) : null}
                  </div>
                </td>
                <td
                  className={`px-3 py-1.5 hidden sm:table-cell ${
                    unauthorized ? UNAUTHORIZED_TEXT : "text-slate-600"
                  }`}
                >
                  {form.filledByName || "—"}
                </td>
                <td
                  className={`px-3 py-1.5 hidden md:table-cell ${
                    unauthorized ? UNAUTHORIZED_TEXT : "text-slate-600"
                  }`}
                >
                  {form.filledByRole || "—"}
                </td>
                <td className="px-3 py-1.5">
                  <ClinicalFormStatusBadge status={form.status} />
                </td>
                <td className="px-3 py-1.5 whitespace-nowrap">
                  {(() => {
                    const scheduleDate = clinicalFormScheduleDate(form);
                    return scheduleDate ? (
                      <span
                        className={`inline-flex items-center gap-1 tabular-nums ${
                          unauthorized ? UNAUTHORIZED_TEXT : "text-slate-700"
                        }`}
                        title={[
                          `${visit?.discipline || ""}${
                            visit?.startTime ? ` at ${visit.startTime}` : ""
                          }`.trim(),
                          visitAuthorizationTitle(visit?.authorization),
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      >
                        {unauthorized ? (
                          <AlertCircleIcon size={11} />
                        ) : (
                          <CalendarIcon size={11} className="text-indigo-500" />
                        )}
                        {displayDateUS(scheduleDate)}
                      </span>
                    ) : (
                      <span className={emDash}>—</span>
                    );
                  })()}
                </td>
                {showMarks && (
                  <td className="px-3 py-1.5 hidden lg:table-cell">
                    <ClinicalFormMarksRow
                      marks={marks}
                      size="sm"
                      emptyClassName={emDash}
                      onRemoveMark={
                        canManageFlags
                          ? async (markType) => {
                              const key = `${form.id}:${markType}`;
                              setRemovingKey(key);
                              try {
                                await onRemoveMark?.(form, markType);
                              } finally {
                                setRemovingKey(null);
                              }
                            }
                          : undefined
                      }
                      removingMarkType={
                        removingKey?.startsWith(`${form.id}:`)
                          ? removingKey.slice(String(form.id).length + 1)
                          : null
                      }
                    />
                  </td>
                )}
                <td className="px-3 py-1.5 whitespace-nowrap">
                  {/* Flex row, so the print link and the buttons sit on one
                      line instead of each finding its own text baseline. */}
                  <div className="flex items-center justify-end gap-0.5">
                    {typeof onPrint === "function" ? (
                      <button
                        type="button"
                        className={actionBtn}
                        onClick={() => onPrint(form)}
                        title="Print"
                        aria-label={`Print ${form.formName}`}
                      >
                        <PrintIcon size={14} />
                      </button>
                    ) : (
                      <a
                        href={clinicalFormPrintUrl(patientId, form.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={actionBtn}
                        title="Print"
                        aria-label={`Print ${form.formName}`}
                      >
                        <PrintIcon size={14} />
                      </a>
                    )}
                    {showAudit ? (
                      <button
                        type="button"
                        className={actionBtn}
                        onClick={() => onViewAudit(form)}
                        title="Audit trail"
                        aria-label={`Audit trail for ${form.formName}`}
                      >
                        <HistoryIcon size={14} />
                      </button>
                    ) : null}
                    {canDelete && (
                      <button
                        type="button"
                        className={`${actionBtn} text-red-600 hover:bg-red-50 hover:text-red-700`}
                        onClick={() => onDelete(form)}
                        title="Delete"
                        aria-label={`Delete ${form.formName}`}
                      >
                        <DeleteIcon size={14} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default ClinicalFormList;
